import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ListView, { ListViewItem } from '@ingka/list-view';
import ProductIdentifier from '@ingka/product-identifier';
import Button from '@ingka/button';
import QuantityStepper from '@ingka/quantity-stepper';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import TrashCanIcon from '@ingka/ssr-icon/paths/trash-can';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import { useLocationProducts } from '@/redux/back-dashboard-api/hooks';
import { LoadingState } from '@/components/helpers/loading';
import { GenericErrorCard } from '@/components/data/error';
import { Location, OrderType } from '@/redux/back-dashboard-api';
import style from './index.module.css';
import { ProductWithStock, getItemCaption } from '@/lib/utils/database';
import { useRouter } from '@/lib/router';
import { useContentTracking } from '@/lib/hooks/tracking';
import { ItemImage } from '@/components/containers/item-image';
import { ActiveLocationChecker } from '@/components/helpers/location';
import {
  useWebshopMode,
  WebshopModeProvider,
} from '@/components/page-widgets/webshop/context';
import { ItemDescription } from '@/components/containers/item-description';

const PreviewItem: FC<{
  item: ProductWithStock['items'][number];
  amount: number;
}> = ({ item, amount }) => {
  // Hooks
  const { t } = useTranslation();
  const { updateItem, getMaxQuantity } = useWebshopMode();
  // Data
  const caption = getItemCaption(item, t);
  const maxVal = getMaxQuantity(item.stock.available);
  const minVal = 1;
  // Event handlers
  const handleUpdateItem = useCallback(
    (id: string, amount: number) => {
      updateItem({ id, amount });
    },
    [updateItem]
  );

  return (
    <div className="flex flex-row">
      <div className="w-[150px] h-[100px] mr-3 mt-5">
        <ItemImage item={item} />
      </div>

      <ListViewItem
        className={style['item__image']}
        title={item.productName}
        description={
          <ItemDescription description={item.description} summarized ellipsis />
        }
        emphasised
        addOn={
          <div className="flex flex-col">
            <span>{caption}</span>
            <ProductIdentifier
              className="mt-1"
              value={`${item.sku}`.padStart(8, '0')}
            />
            <div className="flex flex-row items-center justify-between mt-2">
              <QuantityStepper
                ariaDescribedBy={t('aria.quantityStepper')}
                ariaDescribedById="helper"
                ariaLabelDecrease={t('aria.decrease')}
                ariaLabelIncrease={t('aria.increase')}
                ariaLabelInput={t('quantitySet')}
                defaultValue={amount}
                maxVal={maxVal}
                minVal={minVal}
                value={amount}
                onChange={(event) => {
                  let value = parseInt(event.target.value, 10) || minVal;

                  if (value > maxVal) value = maxVal;
                  if (value < minVal) value = minVal;

                  handleUpdateItem(item._id, value);
                }}
                onIncrease={() => {
                  handleUpdateItem(item._id, amount + 1);
                }}
                onDecrease={() => {
                  handleUpdateItem(item._id, amount - 1);
                }}
              />

              <Button
                iconPosition="leading"
                size="medium"
                ssrIcon={TrashCanIcon}
                iconOnly={true}
                type="tertiary"
                onClick={() => handleUpdateItem(item._id, 0)}
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

const ShoppingBagItems: FC<{
  location: Location;
}> = ({ location }) => {
  // Hooks
  const {
    activeBag: { items },
    showStock,
  } = useWebshopMode();
  const { data, isLoading, error } = useLocationProducts(location, {
    onlyInWarehouse: showStock,
  });
  // Data
  const activeItems = useMemo(
    () =>
      data
        .map(({ items }) => items)
        .flat()
        .map((item) => ({
          item,
          amount: items.find(({ id }) => id === item._id)?.amount || 0,
        }))
        .filter(({ amount }) => amount > 0),
    [data, items]
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <ListView id="article-list" showDivider size="large">
      {activeItems.map((data) => (
        <PreviewItem key={data.item._id} {...data} />
      ))}
    </ListView>
  );
};

const ShoppingBagSiderContent: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const router = useRouter();
  const trackContent = useContentTracking();
  const {
    orderType,
    getCheckoutPath,
    activeBag: shoppingBag,
  } = useWebshopMode();
  // Data
  const { t } = useTranslation();
  const { data: location, isLoading, error } = useActiveLocation();
  const isEmpty = shoppingBag.items.length === 0;

  const handleCheckout = () => {
    router.push(getCheckoutPath());
    handleDismiss();
  };

  useEffect(() => {
    trackContent(
      orderType === OrderType.Inventory ? 'wardrobe-bag' : 'shopping-bag',
      {
        type: 'sider',
        products: shoppingBag.items.length,
      }
    );
  }, []);

  const getKey = (label: string) =>
    `page.shoppingBag.${orderType.toLocaleLowerCase()}.${label}`;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <Sheets
      {...rest}
      data-testid="shoppingBagModal"
      aria-label={t(getKey(`title`))}
      footer={
        <ModalFooter className="space-x-4">
          <Button
            disabled={isEmpty || !location}
            type="primary"
            onClick={handleCheckout}
          >
            {t(
              orderType === OrderType.Inventory
                ? 'label.importWardrobe'
                : 'label.checkout'
            )}
          </Button>
        </ModalFooter>
      }
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t(getKey(`title`))}
        />
      }
    >
      <ModalBody>
        {isEmpty && (
          <>
            <p className="txt mb-2">
              <b>{t(getKey(`emptyTitle`))}</b>
            </p>
            <p>{t(getKey(`emptyDescription`))}</p>
          </>
        )}

        <ActiveLocationChecker />
        {location && <ShoppingBagItems location={location} />}
      </ModalBody>
    </Sheets>
  );
};

const ShoppingBagSider: FC<
  ModalComponentBaseProps & { orderType: OrderType }
> = ({ orderType, ...props }) => (
  <WebshopModeProvider orderType={orderType}>
    <ShoppingBagSiderContent {...props} />
  </WebshopModeProvider>
);

export default ShoppingBagSider;
